import { interpret, type InterpreterFrom } from 'xstate';
import { waitFor } from 'xstate/lib/waitFor';

import { classInstances } from '@sg-core/resources/assets/js/libs/classes';
import { MediaPlayer } from '@sg-core/resources/assets/js/libs/media-player';
import { mergeDeep, lazySelector } from '@sg-core/resources/assets/js/utils';

import { videoStateMachine, videoStateMachineConfig } from './machine/video-state';

import type Plyr from 'plyr';

export interface VideoTrackingOptions {
  milestones?: number[];
  title?: string;
  force_tracking?: boolean;
  skip_videoprogress?: boolean;
  item?: Record<string, unknown>;
}

export interface VideoPlayerOptions {
  tapToToggle?: boolean;
  init?: boolean;
  prefetch?: boolean;
  plyr?: boolean;
  hasPlaylistCapabilities?: boolean;
  videoTracking?: VideoTrackingOptions;
  lazyOptions?: IntersectionObserverInit;
  plyrOptions?: Plyr.Options;
  [key: string]: unknown;
}

const CUSTOM_EVENTS = {
  END: 'videoend',
  PROGRESS: 'videoprogress',
  START: 'videostart',
};

const VIDEO_PLAYER_SELECTOR = '.phxsg-video-player';

/**
 * Mutually exclusive state classes
 */
const EXCLUSIVE_STATES = {
  initialize: `phxsg-is-initializing`,
  idle: `phxsg-is-idle`,
  load: `phxsg-is-loading`,
  pause: `phxsg-is-paused`,
  play: `phxsg-is-playing`,
  lowPower: `phxsg-is-low-power`,
} as const;

export class VideoPlayer extends MediaPlayer {
  protected playerOptions: VideoPlayerOptions;
  protected videoMachine: ReturnType<typeof videoStateMachine.withConfig>;
  protected videoService?: InterpreterFrom<typeof videoStateMachine> | null;
  protected playOverlay?: HTMLElement | null;
  protected ended?: boolean;
  protected started?: boolean;
  protected highestProgress = 0;
  protected milestone = 0;

  private onSurfaceClick = (event: MouseEvent) => {
    const plyr = this.getPlyrInstance();

    if (!plyr?.elements.container?.classList.contains('plyr--is-touch')) {
      return;
    }

    const { target } = event;
    const { container, wrapper } = plyr.elements;

    if (
      !(target instanceof Element) ||
      (target !== container && !wrapper?.contains(target)) ||
      target.closest('button, a, input, select, textarea, [role="button"]')
    ) {
      return;
    }

    if (plyr.paused) {
      void this.play();
    } else {
      this.pause();
    }
  };

  constructor(el: string | Element, options: VideoPlayerOptions = {}) {
    const element = lazySelector(el);
    const closestElement = element.closest(VIDEO_PLAYER_SELECTOR) || element;

    super(closestElement, 'video, iframe');

    classInstances.set(this.mediaElement, this);

    this.element.classList.add('phxsg-is-unplayed');

    /**
     * If videoTracking is accidentally null,
     * it will override the mergeDeep default,
     * so we must provide an empty object.
     */
    if (typeof options.videoTracking !== 'object' || options.videoTracking === null) {
      options.videoTracking = {};
    }

    /**
     * Derive options from the video/iframe element's native DOM attributes.
     * This allows the HTML markup to be the source of truth for player behavior.
     */
    this.playerOptions = this.mergeOptionsWithDOMAttrs(options);

    /**
     * If storage is enabled, Plyr will use the stored values for
     * muted, volume, language, etc. This is not recommended for
     * single Video Player instances, but it's useful for
     * playlists, to store user prefs between episodes.
     */
    if (this.playerOptions.plyrOptions) {
      this.playerOptions.plyrOptions.storage = {
        enabled: this.playerOptions.hasPlaylistCapabilities === true,
      };
    }

    const videoElementForContext = this.mediaElement as unknown as null;
    const context = {
      autoplay: this.playerOptions.autoplay === true,
      enablePlyr: !!this.playerOptions.plyr,
      hls: null,
      lazyOptions: (this.playerOptions.lazyOptions ?? null) as unknown as null,
      intersectionObserver: null,
      mutationObserver: null,
      plyr: null,
      plyrOptions: (this.playerOptions.plyrOptions ?? null) as unknown as null,
      posters: null,
      posterSrc: null,
      prefetch: !!this.playerOptions.prefetch,
      playController: false as const,
      sourceElement: null,
      sources: null,
      videoElement: videoElementForContext,
      videoSrc: null,
    };

    this.videoMachine = videoStateMachine
      .withContext(context)
      .withConfig(videoStateMachineConfig as Parameters<typeof videoStateMachine.withConfig>[0]);

    if (!this.mediaElement) {
      console.warn(`[VideoPlayer] No 'video' or 'iframe' elements were found in ${this.element} node`);
      return;
    }

    if (this.playerOptions.init) {
      this.init();
    }
  }

  /**
   * Derive VideoPlayerOptions from the video/iframe element's native DOM attributes.
   * This allows the HTML markup to be the source of truth for player behavior.
   */
  private mergeOptionsWithDOMAttrs(options: VideoPlayerOptions = {}): VideoPlayerOptions {
    const domOptions: VideoPlayerOptions = {};

    if (this.mediaElement instanceof HTMLVideoElement) {
      domOptions.autoplay = this.mediaElement.autoplay;
      domOptions.muted = this.mediaElement.muted;
      domOptions.loop = this.mediaElement.loop;
      domOptions.playsinline = this.mediaElement.playsInline;

      domOptions.plyrOptions = {
        autoplay: this.mediaElement.autoplay,
        muted: this.mediaElement.muted,
        loop: { active: this.mediaElement.loop },
      };
    }

    return mergeDeep<VideoPlayerOptions>(
      {
        init: true,
        lazyOptions: {
          rootMargin: '200px',
        },
        plyr: true,
        plyrOptions: {
          fullscreen: {
            enabled: true,
            iosNative: true,
          },

          /**
           * If element is an iframe, we are removing
           * the 'play-large' button to avoid any
           * conflicts with the iframe's own controls.
           */
          ...(this.mediaElement instanceof HTMLIFrameElement && {
            controls: [
              'play',
              'progress',
              'current-time',
              'mute',
              'volume',
              'captions',
              'settings',
              'pip',
              'airplay',
              'fullscreen',
            ],
          }),
        },
        videoTracking: {
          // Default milestones
          milestones: [10, 25, 50, 75, 100],
        },
      },
      domOptions,
      options,
    );
  }

  /**
   * Toggle classes on a DOM element based on a mutually exclusive classes
   * map object
   *
   * @param {HTMLElement} element The element on which to toggle classes
   * @param {Object} stateMap A key: value map of classes
   * @param {String} toAdd A string representing a key in the stateMap
   */
  private toggleExclusiveClasses(
    element: HTMLElement,
    stateMap: typeof EXCLUSIVE_STATES,
    toAdd: keyof typeof EXCLUSIVE_STATES,
  ) {
    const { [toAdd]: newState, ...others } = stateMap;

    element.classList.add(newState as string);

    for (const oldState of Object.values(others)) {
      element.classList.remove(oldState as string);
    }
  }

  /**
   * Listen to standard video or Plyr events in order
   * to later dispatch custom events with additional
   * details for CMS tracking purposes.
   */
  private addTrackableEventListeners() {
    if (!this.mediaElement) {
      return false;
    }

    this.ended = false;
    this.highestProgress = 0;
    this.milestone = 0;
    this.onEnded = this.onEnded.bind(this);
    this.onPlay = this.onPlay.bind(this);
    this.onTimeUpdate = this.onTimeUpdate.bind(this);
    this.started = false;

    const plyrInstance = this.getPlyrInstance();

    if (plyrInstance) {
      plyrInstance.on('ended', this.onEnded);
      plyrInstance.on('play', this.onPlay);
      plyrInstance.on('timeupdate', this.onTimeUpdate);
    } else {
      this.mediaElement.addEventListener('ended', this.onEnded);
      this.mediaElement.addEventListener('play', this.onPlay);
      this.mediaElement.addEventListener('timeupdate', this.onTimeUpdate);
    }
  }

  /**
   * @see addTrackableEventListeners()
   */
  private removeTrackableEventListeners() {
    if (!this.mediaElement) {
      return false;
    }

    const plyrInstance = this.getPlyrInstance();

    if (plyrInstance) {
      plyrInstance.off('ended', this.onEnded);
      plyrInstance.off('play', this.onPlay);
      plyrInstance.off('timeupdate', this.onTimeUpdate);
    } else {
      this.mediaElement.removeEventListener('ended', this.onEnded);
      this.mediaElement.removeEventListener('play', this.onPlay);
      this.mediaElement.removeEventListener('timeupdate', this.onTimeUpdate);
    }
  }

  /**
   * Dispatch the video first "ended" event with additional details.
   * @param {Event} event
   */
  protected onEnded(event: Event) {
    if (!this.ended) {
      this.element.dispatchEvent(
        new CustomEvent(CUSTOM_EVENTS.END, {
          bubbles: true,
          composed: true,
          detail: {
            originalEvent: event,
            videoPlayer: this,
            videoTracking: this.playerOptions.videoTracking,
          },
        }),
      );

      this.ended = true;
    }
  }

  /**
   * Dispatch the video first "play" event with additional details.
   * @param {Event} event
   */
  protected onPlay(event: Event) {
    if (!this.started) {
      this.element.dispatchEvent(
        new CustomEvent(CUSTOM_EVENTS.START, {
          bubbles: true,
          composed: true,
          detail: {
            originalEvent: event,
            videoPlayer: this,
            videoTracking: this.playerOptions.videoTracking,
          },
        }),
      );

      this.started = true;
    }
  }

  /**
   * Dispatch a video progress event each time the playback
   * reaches one of the specified milestones.
   * @param {Event} event
   */
  protected onTimeUpdate(event: Event) {
    const plyr = (event as CustomEvent)?.detail?.plyr;
    const duration = plyr ? plyr.duration : this.mediaElement.duration;
    const currentTime = plyr ? plyr.currentTime : this.mediaElement.currentTime;

    if (isNaN(duration)) {
      return;
    }

    let percentage = Math.floor((currentTime * 100) / duration);

    if (percentage === 0 && this.highestProgress >= 99) {
      percentage = 100;
    }

    this.highestProgress = Math.max(percentage, this.highestProgress);

    if (percentage <= this.milestone) {
      return;
    }

    const lastMilestone = this.milestone;
    this.milestone =
      this.playerOptions.videoTracking?.milestones?.findLast((milestone: number) => milestone <= percentage) ?? 0;

    if (this.milestone > lastMilestone) {
      this.element.dispatchEvent(
        new CustomEvent(CUSTOM_EVENTS.PROGRESS, {
          bubbles: true,
          composed: true,
          detail: {
            originalEvent: event,
            progress: this.milestone,
            videoPlayer: this,
            videoTracking: this.playerOptions.videoTracking,
          },
        }),
      );
    }
  }

  /*----------------------------------------*\
    PUBLIC
  \*----------------------------------------*/

  init() {
    const { tapToToggle, plyrOptions } = this.playerOptions;

    // Plyr ignores surface clicks on touch devices when controls auto-hide.
    if (tapToToggle && plyrOptions?.hideControls !== false && plyrOptions?.clickToPlay !== false) {
      this.element.addEventListener('click', this.onSurfaceClick);
    }

    /**
     * Interpret the machine and create the service
     */
    this.videoService = interpret(this.videoMachine);

    /**
     * Create the `player-ready` event that will be fired when the
     * DOM has been updated and playback actions can be taken. It
     * provide this instance as the `{ detail: { videoPlayer }}`
     * property of the event.
     */
    const readyEvent = new CustomEvent('player-ready', {
      bubbles: true,
      detail: { videoPlayer: this },
    });

    const playEvent = new CustomEvent('player-play', {
      bubbles: true,
      detail: { videoPlayer: this },
    });

    const pauseEvent = new CustomEvent('player-pause', {
      bubbles: true,
      detail: { videoPlayer: this },
    });

    /**
     * Define specific behaviors based on the current state of the machine.
     * We choose to handle this here rather than in the machine implementation
     * because these side effects are not mandatory for the machine to work
     * (they don't prevent the machine to work if they are not executed)
     */
    if (this.videoService) {
      this.videoService.onTransition((state, event) => {
        const eventType = (event as { type?: string })?.type;

        if (state.matches('initialize')) {
          this.toggleExclusiveClasses(this.element, EXCLUSIVE_STATES, 'initialize');
        } else if (state.hasTag('loading')) {
          this.toggleExclusiveClasses(this.element, EXCLUSIVE_STATES, 'load');
        } else if (
          state.matches('visible.pause') ||
          state.matches('visible.stop') ||
          state.matches('hidden.pause') ||
          state.matches('hidden.suspend')
        ) {
          this.toggleExclusiveClasses(this.element, EXCLUSIVE_STATES, 'pause');
          this.mediaElement.dispatchEvent(pauseEvent);
        } else if (state.matches('visible.play') && eventType === 'done.invoke.video.visible.play:invocation[0]') {
          /**
           * Audio controller
           */
          this.audioController.onMediaPlay(this);
          this.mediaElement.dispatchEvent(playEvent);
        } else if (state.matches('visible.play')) {
          this.element.classList.remove('phxsg-is-unplayed');
          this.toggleExclusiveClasses(this.element, EXCLUSIVE_STATES, 'play');
          this.mediaElement.dispatchEvent(playEvent);
        } else if (state.matches('visible.lowPower')) {
          this.toggleExclusiveClasses(this.element, EXCLUSIVE_STATES, 'lowPower');
        }

        if (eventType === 'done.invoke.parsePosters' || eventType === 'POSTER_MEDIA_QUERY_CHANGE') {
          this.element.style.cssText = `--video-poster-image: url("${(state as { context?: { posterSrc?: string } })?.context?.posterSrc || ''}");`;
        }

        if (eventType === 'done.invoke.loadSources') {
          this.mediaElement.dispatchEvent(readyEvent);
          this.addTrackableEventListeners();
        }
      });
    }

    /**
     * Overlay and play button
     */
    this.playOverlay = this.element.querySelector('.phxsg-video-player__play-overlaid');

    if (this.playOverlay) {
      this.playOverlay.addEventListener('click', () => this.play(undefined), {
        once: true,
      });
    }

    /**
     * Start the service and 🙏
     */
    if (this.videoService) {
      this.videoService.start();
    }
  }

  /**
   * Play the media
   */
  async play(time?: number) {
    try {
      /**
       * We want to wait for the video to be actually visible in the viewport
       * before playing the video. It can potentially occur when media are
       * embedded in modal or in other offscreen elements
       */
      if (!this.videoService) {
        return;
      }

      await waitFor(this.videoService, (state) => state.matches('visible'));
    } catch {
      console.warn('[VideoPlayer] the service never reached its visible state');
      return;
    }

    /**
     * Add the ability to start the playback at a given time
     */
    if (typeof time === 'number') {
      this.seekTo(time);
    }

    if (this.videoService) {
      this.videoService.send('PLAY');
    }
  }

  /**
   * Show the media. Useful to programmatically play the media
   * while honoring previous user pause action
   */
  show() {
    if (this.videoService) {
      this.videoService.send('SHOW');
    }
  }

  /**
   * Pause the media
   */
  pause() {
    if (this.videoService) {
      this.videoService.send('PAUSE');
    }
  }

  /**
   * Hide the media. Useful to programmatically pause the media
   * while honoring previous user pause action
   */
  hide() {
    if (this.videoService) {
      this.videoService.send('HIDE');
    }
  }

  /**
   * Toggle the media playback
   */
  toggle() {
    if (this.videoService) {
      this.videoService.send('TOGGLE');
    }
  }

  /**
   * Seek the current playback position to a new one
   *
   * @param {Number} value The new media position
   */
  seekTo(value: number) {
    if (this.videoService) {
      this.videoService.send({ type: 'SEEK', value });
    }
  }

  /**
   * Stop and remove the video service to free up memory
   */
  destroy() {
    this.element.removeEventListener('click', this.onSurfaceClick);
    this.audioController.onDestroy(this);
    this.removeTrackableEventListeners();

    if (this.videoService) {
      this.videoService.stop();
      this.videoService = null;
    }

    classInstances.delete(this.mediaElement);

    super.destroy();
  }

  get options() {
    return this.playerOptions;
  }

  get videoState() {
    return this.videoService?.getSnapshot() || null;
  }
}
